import React, { useState, useCallback, useContext, useEffect, useRef } from "react";
import useManagedConnections from "../../hooks/useManagedConnections";
import { mergeManagedConnections } from "./managedConnections";
import { SOCKET_EVENTS, whatsappTransferableTicketsContract } from "@markedesk/core-contracts";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

// import { SocketContext } from "../../context/Socket/SocketContext";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  Button,
  Chip,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Table,
  TableHead,
  Paper,
  Tooltip,
  Typography,
  CircularProgress,
  Box,
  Card,
  CardActionArea,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem as MuiMenuItem,
  FormControl,
  InputLabel,
} from "@material-ui/core";
import {
  Edit,
  CheckCircle,
  SignalCellularConnectedNoInternet2Bar,
  SignalCellularConnectedNoInternet0Bar,
  SignalCellular4Bar,
  CropFree,
  ErrorOutline,
  DeleteOutline,
  Facebook,
  Instagram,
  WhatsApp,
  Sync,
} from "@material-ui/icons";
import ChannelIcon, { useChannelPlugins, nativeChannels } from "../../components/ChannelIcon";
import WebhookIcon from '@mui/icons-material/Webhook';
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";

import PluginBridge from "../../components/PluginBridge";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import formatSerializedId from '../../utils/formatSerializedId';
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import ForbiddenPage from "../../components/ForbiddenPage";
import { Can } from "../../components/Can";
import MessageImportMonitor from "../../components/MessageImportMonitor";
import { isNativeChannelDisabled } from "../../utils/nativeChannels";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    // padding: theme.spacing(1),
    padding: theme.padding,
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  tooltip: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: theme.typography.pxToRem(14),
    border: "1px solid #dadde9",
    maxWidth: 450,
  },
  tooltipPopper: {
    textAlign: "center",
  },
  buttonProgress: {
    color: green[500],
  },
  channelGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 16,
    padding: "8px 0",
  },
  channelCard: {
    textAlign: "center",
    transition: "box-shadow 0.2s, transform 0.15s",
    "&:hover": {
      boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
      transform: "translateY(-2px)",
    },
  },
  channelCardDisabled: {
    opacity: 0.45,
    pointerEvents: "none",
  },
}));

const CustomToolTip = ({ title, content, children }) => {
  const classes = useStyles();

  return (
    <Tooltip
      arrow
      classes={{
        tooltip: classes.tooltip,
        popper: classes.tooltipPopper,
      }}
      title={
        <React.Fragment>
          <Typography gutterBottom color="inherit">
            {title}
          </Typography>
          {content && <Typography>{content}</Typography>}
        </React.Fragment>
      }
    >
      {children}
    </Tooltip>
  );
};


const Connections = () => {
  const classes = useStyles();

  // #4271: a tela de conexões administra o parque inteiro; o contexto global é
  // a lista de OPERAÇÃO, recortada. Ver `managedConnections.js`.
  const { whatsApps: whatsAppsEmOperacao, loading } = useContext(WhatsAppsContext);
  const managedConnections = useManagedConnections();
  const whatsApps = mergeManagedConnections(
    managedConnections,
    whatsAppsEmOperacao
  );
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [channel, setChannel] = useState("whatsapp");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const confirmationModalInitialState = {
    action: "",
    title: "",
    message: "",
    whatsAppId: "",
    open: false,
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(confirmationModalInitialState);
  const [planConfig, setPlanConfig] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [sourceConnection, setSourceConnection] = useState("");
  const [targetConnection, setTargetConnection] = useState("");
  // Exclusão com tickets pendurados: { whatsAppId, count, target } (#4119).
  const [deleteTransfer, setDeleteTransfer] = useState(null);
  // Transferência longa (progresso por socket) antes de excluir: quem excluir ao terminar.
  const deleteAfterTransfer = useRef(null);
  const startDeleteRef = useRef(() => {});
  const [transferProgressModalOpen, setTransferProgressModalOpen] = useState(false);
  const [transferProgress, setTransferProgress] = useState({ current: 0, total: 0, percentage: 0 });
  const [errorModal, setErrorModal] = useState(null); // { name, message }
  const channelPlugins = useChannelPlugins();
  const [newConnModalOpen, setNewConnModalOpen] = useState(false);
  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);

  const companyId = user.companyId;

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      setPlanConfig(planConfigs)
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const responseFacebook = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;

      api
        .post("/facebook", {
          facebookUserId: id,
          facebookUserToken: accessToken,
        })
        .then((response) => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch((error) => {
          toastError(error);
        });
    }
  };

  const responseInstagram = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;

      api
        .post("/facebook", {
          addInstagram: true,
          facebookUserId: id,
          facebookUserToken: accessToken,
        })
        .then((response) => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch((error) => {
          toastError(error);
        });
    }
  };

  // Definido antes do useEffect que o usa: entra na lista de dependencias,
  // que e avaliada no render (uma const declarada depois cairia em TDZ).
  const handleCloseTransferModal = useCallback(() => {
    setTransferModalOpen(false);
    setSourceConnection("");
    setTargetConnection("");
  }, []);

  useEffect(() => {
    // Socket pode ser nulo (logout forçado, instante antes de conectar): sem
    // isto o socket.on lançava e derrubava a tela inteira (11-09-2026).
    if (!socket) return undefined;
    const handleTransferTickets = (data) => {
      if (data.action === "progress") {
        setTransferProgress({
          current: data.current,
          total: data.total,
          percentage: Math.round((data.current / data.total) * 100)
        });
      }
      if (data.action === "completed") {
        setTransferProgressModalOpen(false);
        setTransferProgress({ current: 0, total: 0, percentage: 0 });
        toast.success(`Transferência concluída! ${data.transferred} tickets transferidos com sucesso.`);
        handleCloseTransferModal();
        // Transferência feita para poder excluir: segue para a exclusão (reconta antes).
        const pending = deleteAfterTransfer.current;
        deleteAfterTransfer.current = null;
        if (pending) startDeleteRef.current(pending);
      }
      if (data.action === "error") {
        setTransferProgressModalOpen(false);
        setTransferProgress({ current: 0, total: 0, percentage: 0 });
        deleteAfterTransfer.current = null;
        toast.error("Erro na transferência de tickets.");
      }
    };

    // Sem companyId no nome — o socket já é namespace por empresa. Antes
    // (`transferTickets-${companyId}`) o backend emitia com io.to() num room
    // que existe só dentro do namespace da empresa, mas emitia a partir do
    // `io` raiz — o evento nunca chegava. Corrigido junto (TicketController).
    const event = SOCKET_EVENTS.TICKET.TRANSFER;
    socket.on(event, handleTransferTickets);

    return () => {
      socket.off(event, handleTransferTickets);
    };
  }, [socket, user.companyId, handleCloseTransferModal]);

  const handleStartWhatsAppSession = async (whatsAppId) => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRequestNewQrCode = async (whatsAppId) => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenWhatsAppModal = (channel) => {
    setChannel(channel)
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
  }, [setSelectedWhatsApp, setWhatsAppModalOpen]);

  const handleOpenQrModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, [setQrModalOpen, setSelectedWhatsApp]);

  const handleEditWhatsApp = (whatsApp) => {
    setChannel(whatsApp.channel)
    setSelectedWhatsApp(whatsApp);
    setWhatsAppModalOpen(true);
  };

  const handleSyncTemplates = async (whatsAppId) => {
    await api.get(`/whatsapp/sync-templates/${whatsAppId}`);
  }

  const handleCopyWebhook = (url) => {
    navigator.clipboard.writeText(url); // Copia o token para a área de transferência
  };

  const openInNewTab = url => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === "disconnect") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.disconnectTitle"),
        message: i18n.t("connections.confirmationModal.disconnectMessage"),
        whatsAppId: whatsAppId,
      });
    }

    if (action === "delete") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.deleteTitle"),
        message: i18n.t("connections.confirmationModal.deleteMessage"),
        whatsAppId: whatsAppId,
      });
    }
    if (action === "closedImported") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.closedImportedTitle"),
        message: i18n.t("connections.confirmationModal.closedImportedMessage"),
        whatsAppId: whatsAppId,
      });
    }

    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try {
        await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
      } catch (err) {
        toastError(err);
      }
    }

    if (confirmModalInfo.action === "delete") {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      } catch (err) {
        toastError(err);
      }
    }
    if (confirmModalInfo.action === "closedImported") {
      try {
        await api.post(`/closedimported/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.closedimported"));
      } catch (err) {
        toastError(err);
      }
    }


    setConfirmModalInfo(confirmationModalInitialState);
  };


  const renderImportButton = (whatsApp) => {
    if (whatsApp?.statusImportMessages === "renderButtonCloseTickets") {
      return (
        <Button
          style={{ marginLeft: 12 }}
          size="small"
          variant="outlined"
          color="primary"
          onClick={() => {
            handleOpenConfirmationModal("closedImported", whatsApp.id);
          }}
        >
          {i18n.t("connections.buttons.closedImported")}
        </Button>
      );
    }
  };

  // Nome legível do canal quando não há plugin registrado (sem displayName):
  // "whatsapp_baileys" → "WhatsApp Baileys".
  const channelLabel = (channel) =>
    String(channel || "")
      .split("_")
      .map((p) => (p === "whatsapp" ? "WhatsApp" : p.charAt(0).toUpperCase() + p.slice(1)))
      .join(" ");

  const renderActionButtons = (whatsApp) => {
    // Canais de plugin: se o plugin do canal não está carregado/responde
    // (offline ou removido), não dá pra acionar nada — esconde os botões e
    // mostra um aviso. Canais nativos (whatsapp/oficial) não dependem de plugin.
    if (!(whatsApp.channel in nativeChannels)) {
      const plugin = channelPlugins.find((p) => p.channel === whatsApp.channel);
      if (!plugin || plugin.offline) {
        const nome = plugin?.displayName || channelLabel(whatsApp.channel);
        const aviso = plugin
          ? i18n.t("connections.pluginOffline")
          : i18n.t("connections.pluginNotInstalled");
        return (
          <Typography variant="caption" style={{ color: "#f44336" }}>
            {`Plugin "${nome}" ${aviso}`}
          </Typography>
        );
      }
    }
    return (
      <>
        {whatsApp.status === "qrcode" && (
          <Can
            role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
            perform="connections-page:addConnection"
            yes={() => (
              <>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => handleOpenQrModal(whatsApp)}
                >
                  {i18n.t("connections.buttons.qrcode")}
                </Button>{" "}
                {/* Canais de plugin: sair do estado "qrcode" travado.
                    Desconecta → backend marca DISCONNECTED (libera a UI). */}
                {!(whatsApp.channel in nativeChannels) && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      handleOpenConfirmationModal("disconnect", whatsApp.id);
                    }}
                  >
                    {i18n.t("connections.buttons.disconnect")}
                  </Button>
                )}
              </>
            )}
          />
        )}
        {whatsApp.channel === "whatsapp" && whatsApp.status === "DISCONNECTED" && (
          <Can
            role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
            perform="connections-page:addConnection"
            yes={() => (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => handleStartWhatsAppSession(whatsApp.id)}
                >
                  {i18n.t("connections.buttons.tryAgain")}
                </Button>{" "}
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleRequestNewQrCode(whatsApp.id)}
                >
                  {i18n.t("connections.buttons.newQr")}
                </Button>
              </>
            )}
          />
        )}
        {(whatsApp.channel === "whatsapp" && (whatsApp.status === "CONNECTED" ||
          whatsApp.status === "PAIRING" ||
          whatsApp.status === "TIMEOUT")) && (
            <Can
              role={user.profile}
              perform="connections-page:addConnection"
              yes={() => (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      handleOpenConfirmationModal("disconnect", whatsApp.id);
                    }}
                  >
                    {i18n.t("connections.buttons.disconnect")}
                  </Button>

                  {renderImportButton(whatsApp)}
                </>
              )}
            />
          )}
        {(whatsApp.channel === "whatsapp" && whatsApp.status === "OPENING") && (
          <Button size="small" variant="outlined" disabled color="default">
            {i18n.t("connections.buttons.connecting")}
          </Button>
        )}
        {/* Channel plugins (discord, telegram, etc.) — canais não-nativos */}
        {!(whatsApp.channel in nativeChannels) && whatsApp.status === "CONNECTED" && (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={() => {
              handleOpenConfirmationModal("disconnect", whatsApp.id);
            }}
          >
            {i18n.t("connections.buttons.disconnect")}
          </Button>
        )}
        {!(whatsApp.channel in nativeChannels) && whatsApp.status === "DISCONNECTED" && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={() => handleStartWhatsAppSession(whatsApp.id)}
          >
            Reconectar
          </Button>
        )}
      </>
    );
  };

  const renderStatusToolTips = (whatsApp) => {
    return (
      <PluginBridge
        slot="connection-status"
        actions={false}
        layout="flex"
        context={{
          // Conexão COMPLETA — o slot recebe todos os dados da linha (nome,
          // número, status, sessão, channelData, versão, isDefault, datas...),
          // não só campos cherry-picked. #4137
          whatsappId: whatsApp.id,
          connectionId: whatsApp.id,
          channel: whatsApp.channel,
          status: whatsApp.status,
          name: whatsApp.name,
          number: whatsApp.number,
          isDefault: whatsApp.isDefault,
          channelData: whatsApp.channelData,
          updatedAt: whatsApp.updatedAt,
          connection: whatsApp,
        }}
      >
      <div className={classes.customTableCell}>
        {whatsApp.status === "DISCONNECTED" && whatsApp.channelData?.lastError && (
          <Chip
            size="small"
            color="secondary"
            icon={<ErrorOutline style={{ fontSize: 16 }} />}
            label="Erro"
            onClick={() => {
              const le = whatsApp.channelData.lastError;
              // Compat: lastError pode ser string (legado) ou { message, at }
              setErrorModal({
                name: whatsApp.name,
                message: typeof le === "string" ? le : le?.message,
                at: typeof le === "object" ? le?.at : undefined,
              });
            }}
            style={{ cursor: "pointer" }}
          />
        )}
        {whatsApp.status === "DISCONNECTED" && !whatsApp.channelData?.lastError && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.disconnected.title")}
            content={i18n.t("connections.toolTips.disconnected.content")}
          >
            <SignalCellularConnectedNoInternet0Bar color="secondary" />
          </CustomToolTip>
        )}
        {whatsApp.status === "OPENING" && (
          <CircularProgress size={24} className={classes.buttonProgress} />
        )}
        {whatsApp.status === "qrcode" && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.qrcode.title")}
            content={i18n.t("connections.toolTips.qrcode.content")}
          >
            <CropFree />
          </CustomToolTip>
        )}
        {whatsApp.status === "CONNECTED" && (
          <CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
            <SignalCellular4Bar style={{ color: green[500] }} />
          </CustomToolTip>
        )}
        {(whatsApp.status === "TIMEOUT" || whatsApp.status === "PAIRING") && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.timeout.title")}
            content={i18n.t("connections.toolTips.timeout.content")}
          >
            <SignalCellularConnectedNoInternet2Bar color="secondary" />
          </CustomToolTip>
        )}
      </div>
      </PluginBridge>
    );
  };

  const restartWhatsapps = async () => {

    try {
      await api.post(`/whatsapp-restart/`);
      toast.success(i18n.t("connections.waitConnection"));
    } catch (err) {
      toastError(err);
    }
  }

  const handleOpenTransferModal = () => {
    setTransferModalOpen(true);
  };

  const handleCloseTransferProgressModal = () => {
    setTransferProgressModalOpen(false);
    setTransferProgress({ current: 0, total: 0, percentage: 0 });
  };

  const handleTransferTickets = async () => {
    if (!sourceConnection || !targetConnection) {
      toast.error("Selecione as conexões de origem e destino");
      return;
    }

    if (sourceConnection === targetConnection) {
      toast.error("As conexões de origem e destino devem ser diferentes");
      return;
    }

    try {
      const response = await api.post(`/transfer-tickets`, {
        sourceConnectionId: sourceConnection,
        targetConnectionId: targetConnection
      });

      if (response.data.requiresProgress) {
        setTransferModalOpen(false);
        setTransferProgressModalOpen(true);
        setTransferProgress({ current: 0, total: response.data.totalTickets, percentage: 0 });
      } else {
        toast.success(`Tickets transferidos com sucesso! ${response.data.transferred || 0} tickets transferidos.`);
        handleCloseTransferModal();
      }
    } catch (err) {
      toastError(err);
    }
  };

  /**
   * Excluir só sem tickets pendurados (#4119): conta os tickets da conexão; se
   * houver, pede o destino e transfere antes; sem nenhum, vai à confirmação.
   * O backend recusa com 409 do mesmo jeito — isto é o caminho, lá é a garantia.
   */
  const startDelete = async (whatsAppId) => {
    try {
      const { data } = await api.get(whatsappTransferableTicketsContract.buildRoute(whatsAppId));
      if (data.count > 0) {
        setDeleteTransfer({ whatsAppId, count: data.count, target: "" });
      } else {
        handleOpenConfirmationModal("delete", whatsAppId);
      }
    } catch (err) {
      toastError(err);
    }
  };
  startDeleteRef.current = startDelete;

  const handleOpenPreDeleteModal = (whatsAppId) => startDelete(whatsAppId);

  const handleCloseDeleteTransfer = () => setDeleteTransfer(null);

  const handleTransferBeforeDelete = async () => {
    const { whatsAppId, target } = deleteTransfer;
    if (!target) return;
    try {
      const { data } = await api.post("/transfer-tickets", {
        sourceConnectionId: whatsAppId,
        targetConnectionId: target
      });
      setDeleteTransfer(null);
      if (data.requiresProgress) {
        deleteAfterTransfer.current = whatsAppId;
        setTransferProgressModalOpen(true);
        setTransferProgress({ current: 0, total: data.totalTickets, percentage: 0 });
      } else {
        toast.success(i18n.t("connections.deleteTransfer.transferred", { count: data.transferred || 0 }));
        // Reconta: se entrou ticket novo no meio, a janela volta com o número atual.
        startDelete(whatsAppId);
      }
    } catch (err) {
      toastError(err);
    }
  };

  const deleteTargets = deleteTransfer ? whatsApps.filter(w => w.id !== deleteTransfer.whatsAppId) : [];

  return (
    <MainContainer>
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleSubmitConfirmationModal}
        confirmationText={
          confirmModalInfo.action === "delete"
            ? i18n.t("confirmationModal.deleteWord")
            : undefined
        }
      >
        {confirmModalInfo.message}
      </ConfirmationModal>
      {/* Modal de Nova Conexao */}
      <Dialog
        open={newConnModalOpen}
        onClose={() => setNewConnModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Nova Conexao</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            Selecione o canal para criar uma nova conexao
          </Typography>
          <div className={classes.channelGrid}>
            {/* WhatsApp (Baileys interno) */}
            {!isNativeChannelDisabled("whatsapp") && (
              <Card
                variant="outlined"
                className={`${classes.channelCard} ${!planConfig?.plan?.useWhatsapp ? classes.channelCardDisabled : ""}`}
              >
                <CardActionArea
                  onClick={() => {
                    setNewConnModalOpen(false);
                    handleOpenWhatsAppModal();
                  }}
                  style={{ padding: 20 }}
                >
                  <WhatsApp style={{ fontSize: 40, color: "#25D366" }} />
                  <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                    WhatsApp
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Baileys
                  </Typography>
                </CardActionArea>
              </Card>
            )}

            {/* WhatsApp Oficial */}
            <Card
              variant="outlined"
              className={`${classes.channelCard} ${!planConfig?.plan?.useWhatsappOfficial ? classes.channelCardDisabled : ""}`}
            >
              <CardActionArea
                onClick={() => {
                  setNewConnModalOpen(false);
                  handleOpenWhatsAppModal("whatsapp_oficial");
                }}
                style={{ padding: 20 }}
              >
                <WhatsApp style={{ fontSize: 40, color: "#128C7E" }} />
                <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                  WhatsApp Oficial
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Cloud API
                </Typography>
              </CardActionArea>
            </Card>

            {/* Facebook */}
            <FacebookLogin
              appId={process.env.REACT_APP_FACEBOOK_APP_ID}
              autoLoad={false}
              fields="name,email,picture"
              version="9.0"
              scope={process.env.REACT_APP_REQUIRE_BUSINESS_MANAGEMENT?.toUpperCase() === "TRUE"
                ? "public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management"
                : "public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement"}
              callback={responseFacebook}
              render={(renderProps) => (
                <Card
                  variant="outlined"
                  className={`${classes.channelCard} ${!planConfig?.plan?.useFacebook ? classes.channelCardDisabled : ""}`}
                >
                  <CardActionArea
                    onClick={() => {
                      setNewConnModalOpen(false);
                      renderProps.onClick();
                    }}
                    style={{ padding: 20 }}
                  >
                    <Facebook style={{ fontSize: 40, color: "#3b5998" }} />
                    <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                      Facebook
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Messenger
                    </Typography>
                  </CardActionArea>
                </Card>
              )}
            />

            {/* Instagram */}
            <FacebookLogin
              appId={process.env.REACT_APP_FACEBOOK_APP_ID}
              autoLoad={false}
              fields="name,email,picture"
              version="9.0"
              scope={process.env.REACT_APP_REQUIRE_BUSINESS_MANAGEMENT?.toUpperCase() === "TRUE"
                ? "public_profile,instagram_basic,instagram_manage_messages,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management"
                : "public_profile,instagram_basic,instagram_manage_messages,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement"}
              callback={responseInstagram}
              render={(renderProps) => (
                <Card
                  variant="outlined"
                  className={`${classes.channelCard} ${!planConfig?.plan?.useInstagram ? classes.channelCardDisabled : ""}`}
                >
                  <CardActionArea
                    onClick={() => {
                      setNewConnModalOpen(false);
                      renderProps.onClick();
                    }}
                    style={{ padding: 20 }}
                  >
                    <Instagram style={{ fontSize: 40, color: "#e1306c" }} />
                    <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                      Instagram
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Direct
                    </Typography>
                  </CardActionArea>
                </Card>
              )}
            />

            {/* Plugins dinamicos */}
            {channelPlugins.filter((plugin) => plugin.provides?.channel || plugin.channel).map((plugin) => (
              <Card key={plugin.channel} variant="outlined" className={classes.channelCard}>
                <CardActionArea
                  onClick={() => {
                    setNewConnModalOpen(false);
                    handleOpenWhatsAppModal(plugin.channel);
                  }}
                  style={{ padding: 20 }}
                >
                  <ChannelIcon channel={plugin.channel} style={{ fontSize: 40 }} />
                  <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                    {plugin.displayName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {plugin.isRemote ? "Remoto" : "Plugin"}
                  </Typography>
                </CardActionArea>
              </Card>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewConnModalOpen(false)}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {qrModalOpen && (
        <QrcodeModal
          open={qrModalOpen}
          onClose={handleCloseQrModal}
          whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
        />
      )}
      <WhatsAppModal
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
        channel={channel}
      />
      <Dialog
        open={!!errorModal}
        onClose={() => setErrorModal(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Erro na conexão {errorModal?.name ? `· ${errorModal.name}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {errorModal?.at && (
            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
              Ocorrido em {new Date(errorModal.at).toLocaleString()}
            </Typography>
          )}
          <div
            style={{
              fontFamily: "monospace",
              fontSize: 13,
              background: "#1e1e1e",
              color: "#f48771",
              borderRadius: 4,
              padding: 12,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {errorModal?.message}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorModal(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={transferModalOpen}
        onClose={handleCloseTransferModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Transferência de Tickets</DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 24, lineHeight: 1.6 }}>
            Para transferir os tickets, selecione a conexão de <strong>origem</strong> (de onde os tickets serão movidos) 
            e a conexão de <strong>destino</strong> (para onde os tickets serão transferidos). 
            Todos os atendimentos ativos da conexão de origem serão movidos para a conexão de destino.
          </Typography>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 24 }}>
            <FormControl fullWidth>
              <InputLabel>Origem</InputLabel>
              <Select
                value={sourceConnection}
                onChange={(e) => setSourceConnection(e.target.value)}
                label="Origem"
              >
                {whatsApps.map((whatsApp) => (
                  <MuiMenuItem key={whatsApp.id} value={whatsApp.id}>
                    {whatsApp.name}
                  </MuiMenuItem>
                ))}
              </Select>
            </FormControl>

            <div style={{ fontSize: 24, color: '#4caf50', fontWeight: 'bold' }}>
              →
            </div>

            <FormControl fullWidth>
              <InputLabel>Destino</InputLabel>
              <Select
                value={targetConnection}
                onChange={(e) => setTargetConnection(e.target.value)}
                label="Destino"
              >
                {whatsApps.map((whatsApp) => (
                  <MuiMenuItem key={whatsApp.id} value={whatsApp.id}>
                    {whatsApp.name}
                  </MuiMenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTransferModal} color="default">
            CANCELAR
          </Button>
          <Button onClick={handleTransferTickets} color="primary" variant="contained">
            TRANSFERIR
                     </Button>
         </DialogActions>
       </Dialog>
       <Dialog
         open={transferProgressModalOpen}
         onClose={handleCloseTransferProgressModal}
         maxWidth="sm"
         fullWidth
         disableBackdropClick
         disableEscapeKeyDown
       >
         <DialogTitle>Transferindo Tickets</DialogTitle>
         <DialogContent>
           <div style={{ textAlign: 'center', padding: '20px 0' }}>
             <Typography variant="h6" style={{ marginBottom: 16 }}>
               Progresso da Transferência
             </Typography>
             
             <Box position="relative" display="inline-flex" marginBottom={2}>
               <CircularProgress 
                 variant="determinate" 
                 value={transferProgress.percentage} 
                 size={80}
                 thickness={4}
               />
               <Box
                 top={0}
                 left={0}
                 bottom={0}
                 right={0}
                 position="absolute"
                 display="flex"
                 alignItems="center"
                 justifyContent="center"
               >
                 <Typography variant="caption" component="div" color="textSecondary" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                   {transferProgress.percentage}%
                 </Typography>
               </Box>
             </Box>

             <Typography variant="body1" style={{ marginTop: 16 }}>
               {transferProgress.current} de {transferProgress.total} tickets transferidos
             </Typography>
             
             <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
               Por favor, aguarde enquanto os tickets são transferidos...
             </Typography>
           </div>
         </DialogContent>
       </Dialog>
       <Dialog
         open={!!deleteTransfer}
         onClose={handleCloseDeleteTransfer}
         maxWidth="sm"
         fullWidth
       >
         <DialogTitle>{i18n.t("connections.deleteTransfer.title")}</DialogTitle>
         <DialogContent>
           <Typography variant="body1" style={{ marginBottom: 16, lineHeight: 1.6 }}>
             {i18n.t("connections.deleteTransfer.message", { count: deleteTransfer?.count || 0 })}
           </Typography>
           {deleteTargets.length > 0 ? (
             <FormControl fullWidth variant="outlined">
               <InputLabel>{i18n.t("connections.deleteTransfer.target")}</InputLabel>
               <Select
                 value={deleteTransfer?.target || ""}
                 onChange={(e) => setDeleteTransfer(prev => ({ ...prev, target: e.target.value }))}
                 label={i18n.t("connections.deleteTransfer.target")}
               >
                 {deleteTargets.map((whatsApp) => (
                   <MuiMenuItem key={whatsApp.id} value={whatsApp.id}>
                     {whatsApp.name}
                   </MuiMenuItem>
                 ))}
               </Select>
             </FormControl>
           ) : (
             <Typography variant="body2" color="textSecondary">
               {i18n.t("connections.deleteTransfer.noTarget")}
             </Typography>
           )}
         </DialogContent>
         <DialogActions>
           <Button onClick={handleCloseDeleteTransfer}>
             {i18n.t("connections.deleteTransfer.cancel")}
           </Button>
           <Button
             onClick={handleTransferBeforeDelete}
             color="primary"
             variant="contained"
             disabled={!deleteTransfer?.target}
           >
             {i18n.t("connections.deleteTransfer.transfer")}
           </Button>
         </DialogActions>
       </Dialog>
      {user.profile === "user" && user.allowConnections === "disabled" ?
        <ForbiddenPage />
        :
        <>
          <MainHeader>
            <Title>{i18n.t("connections.title")} ({whatsApps.length})</Title>
            <MainHeaderButtonsWrapper>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenTransferModal}
              >
                Transferir Tickets
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={restartWhatsapps}
              >
                {i18n.t("connections.restartConnections")}
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={() => openInNewTab(`https://wa.me/${process.env.REACT_APP_NUMBER_SUPPORT}`)}
              >
                {i18n.t("connections.callSupport")}
              </Button>
              <Can
                role={user.profile}
                perform="connections-page:addConnection"
                yes={() => (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setNewConnModalOpen(true)}
                  >
                    {i18n.t("connections.newConnection")}
                  </Button>
                )}
              />
            </MainHeaderButtonsWrapper>
          </MainHeader>

          <MessageImportMonitor />

          <Paper className={classes.mainPaper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">Channel</TableCell>
                  <TableCell align="center">{i18n.t("connections.table.color")}</TableCell>
                  <TableCell align="center">{i18n.t("connections.table.name")}</TableCell>
                  <TableCell align="center">{i18n.t("connections.table.number")}</TableCell>
                  <TableCell align="center">{i18n.t("connections.table.status")}</TableCell>
                  <TableCell align="center">{i18n.t("connections.table.session")}</TableCell>
                  <TableCell align="center">{i18n.t("connections.table.lastUpdate")}</TableCell>
                  <TableCell align="center">{i18n.t("connections.table.version")}</TableCell>
                  <TableCell align="center">{i18n.t("connections.table.default")}</TableCell>
                  <Can
                    role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
                    perform="connections-page:addConnection"
                    yes={() => (
                      <TableCell align="center" style={{ minWidth: 180 }}>{i18n.t("connections.table.actions")}</TableCell>
                    )}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRowSkeleton />
                ) : (
                  <>
                    {whatsApps?.length > 0 &&
                      whatsApps.map((whatsApp) => (
                        <TableRow key={whatsApp.id}>
                          <TableCell align="center"><ChannelIcon channel={whatsApp.channel} /></TableCell>
                          <TableCell align="center">
                            <div className={classes.customTableCell}>
                              <span
                                style={{
                                  backgroundColor: whatsApp.color,
                                  width: 60,
                                  height: 20,
                                  alignSelf: "center",
                                }}
                              />
                            </div>
                          </TableCell>
                          <TableCell align="center">
                            {whatsApp.name}
                            {!(whatsApp.channel in nativeChannels) && (
                              <Chip
                                size="small"
                                label={
                                  channelPlugins.find((p) => p.channel === whatsApp.channel)
                                    ?.displayName || channelLabel(whatsApp.channel)
                                }
                                style={{ marginLeft: 6, height: 18, fontSize: 11 }}
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">{whatsApp.number && whatsApp.channel === 'whatsapp' ? (<>{formatSerializedId(whatsApp.number)}</>) : whatsApp.number}</TableCell>
                          <TableCell align="center">{renderStatusToolTips(whatsApp)}</TableCell>
                          <TableCell align="center">
                            <PluginBridge
                              slot="connection-actions"
                              actions={false}
                              layout="flex"
                              context={{
                                whatsappId: whatsApp.id,
                                connectionId: whatsApp.id,
                                channel: whatsApp.channel,
                                status: whatsApp.status,
                                name: whatsApp.name,
                                number: whatsApp.number,
                                isDefault: whatsApp.isDefault,
                                channelData: whatsApp.channelData,
                                updatedAt: whatsApp.updatedAt,
                                connection: whatsApp,
                              }}
                            >
                              {renderActionButtons(whatsApp)}
                            </PluginBridge>
                          </TableCell>
                          <TableCell align="center">{format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}</TableCell>
                          <TableCell align="center">
                            {whatsApp.channel === "whatsapp"
                              ? (whatsApp.whatsappVersion?.version || i18n.t("connections.table.defaultVersion"))
                              : "-"}
                          </TableCell>
                          <TableCell align="center">
                            {whatsApp.isDefault && (
                              <div className={classes.customTableCell}>
                                <CheckCircle style={{ color: green[500] }} />
                              </div>
                            )}
                          </TableCell>
                          <Can
                            role={user.profile}
                            perform="connections-page:addConnection"
                            yes={() => (
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditWhatsApp(whatsApp)}
                                >
                                  <Edit />
                                </IconButton>

                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    handleOpenPreDeleteModal(whatsApp.id);
                                  }}
                                >
                                  <DeleteOutline />
                                </IconButton>
                                {whatsApp.channel === "whatsapp_oficial" && (
                                  <>
                                    <Tooltip title="Sincronizar templates">
                                      <IconButton
                                        size="small"
                                        aria-label="sync-templates"
                                        onClick={(e) => {
                                          handleSyncTemplates(whatsApp.id);
                                        }}
                                      >
                                        <Sync />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Copiar webhook para Meta">
                                      <IconButton
                                        size="small"
                                        aria-label="copy-webhook"
                                        onClick={(e) => {
                                          handleCopyWebhook(whatsApp.waba_webhook);
                                        }}
                                      >
                                        <WebhookIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </TableCell>
                            )}
                          />
                        </TableRow>
                      ))}
                  </>
                )}
              </TableBody>
            </Table>
          </Paper>

        </>
      }
    </MainContainer >

  );
};

export default Connections;